import { defineStore } from 'pinia';
import apiClient from '@/util/apiClient';

export interface AnnouncementInput {
	name: string;
	text: string;
	announcementType: 'system' | 'temp';
	expiresOn: string | null;
}

export interface Announcement extends AnnouncementInput {
	id: number;
	createdBy: number;
	updatedBy: number;
	createdOn: string;
	updatedOn: string;
}

// The API's offset-free timestamps represent UTC, never browser local time.
export function announcementDate(value: string): Date {
	return new Date(/[zZ]$|[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`);
}

export function isAnnouncementActive(item: AnnouncementInput, now = Date.now()): boolean {
	return item.announcementType === 'system' || (!!item.expiresOn && announcementDate(item.expiresOn).getTime() > now);
}

export function toLocalExpiration(value: string | null): string {
	if (!value) return '';
	const date = announcementDate(value);
	const pad = (n: number) => String(n).padStart(2, '0');
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export function toUtcExpiration(value: string): string {
	const date = new Date(value);
	if (!value || !Number.isFinite(date.getTime())) throw new Error('Choose a valid expiration date and time.');
	return date.toISOString().slice(0, -1);
}

export function announcementError(error: unknown, fallback: string): string {
	const response = (error as { response?: { data?: { error?: unknown; message?: unknown } } } | null)?.response?.data;
	const message = response?.error || response?.message;
	return typeof message === 'string' ? message : fallback;
}

export interface AnnouncementLink {
	label: string;
	route: string;
	hover: string;
}
export interface AnnouncementIcon {
	name: string;
	hover: string;
}
export interface AnnouncementContent {
	message: string;
	links: AnnouncementLink[];
	icons: AnnouncementIcon[];
}

export const announcementIcons = [
	{ label: 'Information', value: 'circle-info' },
	{ label: 'Warning', value: 'triangle-exclamation' },
	{ label: 'Check', value: 'circle-check' },
	{ label: 'Calendar', value: 'calendar-days' },
	{ label: 'Announcement', value: 'bullhorn' }
];
const prefix = 'EGS_ANNOUNCEMENT_V1:';
const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
export const isAnnouncementRoute = (value: string): boolean => /^\/app(?:\/|[?#]|$)/.test(value) && !/[\\\s\u0000-\u001f]/.test(value);

/** Structured content lives in the existing API text field; legacy plain text stays valid. */
export function readAnnouncementContent(text: string): AnnouncementContent {
	const fallback = { message: text, links: [], icons: [] };
	if (!text.startsWith(prefix)) return fallback;
	try {
		const value: unknown = JSON.parse(text.slice(prefix.length));
		if (!isObject(value) || typeof value['message'] !== 'string' || !Array.isArray(value['links']) || !Array.isArray(value['icons'])) return fallback;
		const links: AnnouncementLink[] = [];
		const icons: AnnouncementIcon[] = [];
		for (const link of value['links'] as unknown[]) {
			if (isObject(link) && typeof link['label'] === 'string' && link['label'].trim() && typeof link['route'] === 'string' && isAnnouncementRoute(link['route'])) {
				links.push({ label: link['label'], route: link['route'], hover: typeof link['hover'] === 'string' ? link['hover'] : '' });
			}
		}
		for (const icon of value['icons'] as unknown[]) {
			if (isObject(icon) && typeof icon['name'] === 'string' && announcementIcons.some((option) => option.value === icon['name']) && typeof icon['hover'] === 'string' && icon['hover'].trim()) {
				icons.push({ name: icon['name'], hover: icon['hover'] });
			}
		}
		return { message: value['message'], links, icons };
	} catch {
		return fallback;
	}
}

export function writeAnnouncementContent(content: AnnouncementContent): string {
	const message = content.message.trim();
	if (!content.links.length && !content.icons.length && !message.startsWith(prefix)) return message;
	return (
		prefix + JSON.stringify({ message, links: content.links.map((link) => ({ label: link.label.trim(), route: link.route.trim(), hover: link.hover.trim() })), icons: content.icons.map((icon) => ({ name: icon.name, hover: icon.hover.trim() })) })
	);
}

export const profileAnnouncementContent: AnnouncementContent = {
	message: 'Your Profile is Incomplete',
	icons: [{ name: 'circle-info', hover: 'A profile is considered complete if the following are filled in: First & Last name, date of birth, gender, primary email and primary phone number' }],
	links: [{ label: 'Complete it now.', route: '/app/profile', hover: '' }]
};

export const useAnnouncementsStore = defineStore('announcements', {
	state: () => ({
		active: [] as Announcement[],
		all: [] as Announcement[],
		error: '',
		managementError: '',
		loadingAll: false,
		allLoaded: false,
		session: null as number | null,
		revision: 0,
		allRevision: 0
	}),
	actions: {
		setSession(userNumber: number | null) {
			this.revision++;
			this.allRevision++;
			this.session = userNumber;
			this.active = [];
			this.all = [];
			this.error = '';
			this.managementError = '';
			this.allLoaded = false;
			this.loadingAll = false;
		},
		async refresh() {
			if (this.session == null) return;
			const revision = ++this.revision;
			try {
				const { data: records } = await apiClient.get<Announcement[]>('/announcements/active');
				if (revision !== this.revision) return;
				this.active = records;
				this.error = '';
			} catch (error) {
				if (revision !== this.revision) return;
				this.error = announcementError(error, 'Announcements could not be loaded.');
			}
		},
		async loadAll() {
			const revision = ++this.allRevision;
			this.loadingAll = true;
			this.managementError = '';
			try {
				const { data: records } = await apiClient.get<Announcement[]>('/announcements/all');
				if (revision !== this.allRevision) return;
				this.all = records;
				this.allLoaded = true;
			} catch (error) {
				if (revision !== this.allRevision) return;
				this.managementError = announcementError(error, 'Could not load announcements. Please retry.');
			} finally {
				if (revision === this.allRevision) this.loadingAll = false;
			}
		},
		async save(input: AnnouncementInput, id?: number): Promise<Announcement> {
			const session = this.session;
			const { data: saved } = id == null ? await apiClient.post<Announcement>('/announcements/save', input) : await apiClient.patch<Announcement>(`/announcements/edit/${id}`, input);
			if (session !== this.session) return saved;
			this.allRevision++;
			this.loadingAll = false;
			const index = this.all.findIndex((item) => item.id === saved.id);
			if (index < 0) this.all.unshift(saved);
			else this.all.splice(index, 1, saved);
			this.allLoaded = true;
			this.revision++;
			this.active = this.active.filter((item) => item.id !== saved.id);
			if (isAnnouncementActive(saved)) this.active.unshift(saved);
			return saved;
		},
		async remove(id: number): Promise<void> {
			const session = this.session;
			await apiClient.delete(`/announcements/delete/${id}`);
			if (session !== this.session) return;
			this.allRevision++;
			this.loadingAll = false;
			this.all = this.all.filter((item) => item.id !== id);
			this.revision++;
			this.active = this.active.filter((item) => item.id !== id);
		}
	}
});
